// Static class maps for Box. Every value is a literal because Tailwind's
// scanner cannot see template-composed class names.

// ---------------------------------------------------------------------------
// Breakpoints
//
// Tailwind's own set, verbatim from `tailwindcss/theme.css`. This package adds
// none and renames none, so a Box breakpoint means exactly what the same prefix
// means in a hand-written class:
//
//   base  —  no media query (Tailwind's unprefixed utility)
//   sm    —  40rem      lg  —  64rem      2xl —  96rem
//   md    —  48rem      xl  —  80rem
//
// `base` is not a Tailwind breakpoint; it is the name the object form needs for
// the unprefixed value. THIS TUPLE IS THE ONE SOURCE OF THE SET, in ascending
// order: the resolver iterates it, `scripts/generate-box-responsive.ts` reads it
// to emit the `@source inline()` block, and `box-responsive.css` mirrors it.
// ---------------------------------------------------------------------------
export const BREAKPOINTS = ["base", "sm", "md", "lg", "xl", "2xl"] as const;
export type Breakpoint = (typeof BREAKPOINTS)[number];

/**
 * The value props, and the CSS property each one drives.
 *
 * These hold arbitrary numbers and strings rather than tokens, so no class can
 * exist for them: a scalar rides inline `style`, and a responsive one rides a
 * `--box-<key>[-<breakpoint>]` custom property. THIS TABLE IS THE ONE SOURCE —
 * `box-responsive.ts` reads it to name the variables it writes, and
 * `scripts/build-box-responsive.ts` reads it to emit the matching media
 * queries. A key added here appears on both sides or on neither.
 *
 * `insetX`/`insetY` are absent because they are shorthands: Box expands them to
 * their two edges before reaching this table.
 */
export const STYLE_VAR_PROPERTIES = {
  w: "width",
  h: "height",
  minw: "min-width",
  minh: "min-height",
  maxw: "max-width",
  maxh: "max-height",
  inset: "inset",
  top: "top",
  right: "right",
  bottom: "bottom",
  left: "left",
  z: "z-index",
  gap: "gap",
  gapx: "column-gap",
  gapy: "row-gap",
} as const;
export type StyleVarKey = keyof typeof STYLE_VAR_PROPERTIES;

/** `z-index` is the one value that must NOT be given a unit. */
export const UNITLESS_STYLE_VARS: readonly StyleVarKey[] = ["z"];

/**
 * A scalar, or per-breakpoint values.
 *
 * An unspecified breakpoint inherits the nearest specified one below it, which
 * is plain mobile-first cascade rather than logic this library adds.
 *
 * The runtime tells the two apart with `isBreakpointObject`, which holds only
 * because NO scalar Box prop value is a plain object — the sole non-primitive
 * prop values are arrays (`border`, `roundedSide`, `resize`). A future
 * object-valued Box prop breaks every responsive prop on the component at once.
 */
export type Responsive<T> = T | Partial<Record<Breakpoint, T>>;

// 0 / 2 / 4 / 8 / 12 / 16 / 24 / 32 / 48. `base` is the 12px step: on the web
// it is the most common compact row and card inset, and without it a call site
// that wants 12 has to nest two padding layers (8 outside, 4 inside) to fake it
// — which is how a header edge and a footer edge end up 4px apart.
export type SpacingToken =
  | "none" | "2xs" | "xs" | "sm" | "base" | "md" | "lg" | "xl" | "2xl";

export const BOX_GAP: Record<SpacingToken, string> = {
  none: "gap-0", "2xs": "gap-0.5", xs: "gap-1", sm: "gap-2",
  base: "gap-3", md: "gap-4", lg: "gap-6", xl: "gap-8", "2xl": "gap-12",
};
export const BOX_GAP_X: Record<SpacingToken, string> = {
  none: "gap-x-0", "2xs": "gap-x-0.5", xs: "gap-x-1", sm: "gap-x-2",
  base: "gap-x-3", md: "gap-x-4", lg: "gap-x-6", xl: "gap-x-8", "2xl": "gap-x-12",
};
export const BOX_GAP_Y: Record<SpacingToken, string> = {
  none: "gap-y-0", "2xs": "gap-y-0.5", xs: "gap-y-1", sm: "gap-y-2",
  base: "gap-y-3", md: "gap-y-4", lg: "gap-y-6", xl: "gap-y-8", "2xl": "gap-y-12",
};

export const BOX_PADDING: Record<SpacingToken, string> = {
  none: "p-0", "2xs": "p-0.5", xs: "p-1", sm: "p-2",
  base: "p-3", md: "p-4", lg: "p-6", xl: "p-8", "2xl": "p-12",
};
export const BOX_PADDING_X: Record<SpacingToken, string> = {
  none: "px-0", "2xs": "px-0.5", xs: "px-1", sm: "px-2",
  base: "px-3", md: "px-4", lg: "px-6", xl: "px-8", "2xl": "px-12",
};
export const BOX_PADDING_Y: Record<SpacingToken, string> = {
  none: "py-0", "2xs": "py-0.5", xs: "py-1", sm: "py-2",
  base: "py-3", md: "py-4", lg: "py-6", xl: "py-8", "2xl": "py-12",
};
export const BOX_PADDING_TOP: Record<SpacingToken, string> = {
  none: "pt-0", "2xs": "pt-0.5", xs: "pt-1", sm: "pt-2",
  base: "pt-3", md: "pt-4", lg: "pt-6", xl: "pt-8", "2xl": "pt-12",
};
export const BOX_PADDING_BOTTOM: Record<SpacingToken, string> = {
  none: "pb-0", "2xs": "pb-0.5", xs: "pb-1", sm: "pb-2",
  base: "pb-3", md: "pb-4", lg: "pb-6", xl: "pb-8", "2xl": "pb-12",
};
export const BOX_PADDING_LEFT: Record<SpacingToken, string> = {
  none: "pl-0", "2xs": "pl-0.5", xs: "pl-1", sm: "pl-2",
  base: "pl-3", md: "pl-4", lg: "pl-6", xl: "pl-8", "2xl": "pl-12",
};
export const BOX_PADDING_RIGHT: Record<SpacingToken, string> = {
  none: "pr-0", "2xs": "pr-0.5", xs: "pr-1", sm: "pr-2",
  base: "pr-3", md: "pr-4", lg: "pr-6", xl: "pr-8", "2xl": "pr-12",
};

// Margin. Negative tokens exist because pulling a child back out of its
// parent's padding is a real layout need (full-bleed rows inside a padded
// shell) that no padding prop can express.
export type MarginToken = SpacingToken | "auto";
export const BOX_MARGIN: Record<MarginToken, string> = {
  none: "m-0", "2xs": "m-0.5", xs: "m-1", sm: "m-2",
  base: "m-3", md: "m-4", lg: "m-6", xl: "m-8", "2xl": "m-12", auto: "m-auto",
};
export const BOX_MARGIN_X: Record<MarginToken, string> = {
  none: "mx-0", "2xs": "mx-0.5", xs: "mx-1", sm: "mx-2",
  base: "mx-3", md: "mx-4", lg: "mx-6", xl: "mx-8", "2xl": "mx-12", auto: "mx-auto",
};
export const BOX_MARGIN_Y: Record<MarginToken, string> = {
  none: "my-0", "2xs": "my-0.5", xs: "my-1", sm: "my-2",
  base: "my-3", md: "my-4", lg: "my-6", xl: "my-8", "2xl": "my-12", auto: "my-auto",
};
export const BOX_MARGIN_TOP: Record<MarginToken, string> = {
  none: "mt-0", "2xs": "mt-0.5", xs: "mt-1", sm: "mt-2",
  base: "mt-3", md: "mt-4", lg: "mt-6", xl: "mt-8", "2xl": "mt-12", auto: "mt-auto",
};
export const BOX_MARGIN_BOTTOM: Record<MarginToken, string> = {
  none: "mb-0", "2xs": "mb-0.5", xs: "mb-1", sm: "mb-2",
  base: "mb-3", md: "mb-4", lg: "mb-6", xl: "mb-8", "2xl": "mb-12", auto: "mb-auto",
};
export const BOX_MARGIN_LEFT: Record<MarginToken, string> = {
  none: "ml-0", "2xs": "ml-0.5", xs: "ml-1", sm: "ml-2",
  base: "ml-3", md: "ml-4", lg: "ml-6", xl: "ml-8", "2xl": "ml-12", auto: "ml-auto",
};
export const BOX_MARGIN_RIGHT: Record<MarginToken, string> = {
  none: "mr-0", "2xs": "mr-0.5", xs: "mr-1", sm: "mr-2",
  base: "mr-3", md: "mr-4", lg: "mr-6", xl: "mr-8", "2xl": "mr-12", auto: "mr-auto",
};

export type NegativeSpacingToken = Exclude<SpacingToken, "none">;
export const BOX_MARGIN_X_NEGATIVE: Record<NegativeSpacingToken, string> = {
  "2xs": "-mx-0.5", xs: "-mx-1", sm: "-mx-2",
  base: "-mx-3", md: "-mx-4", lg: "-mx-6", xl: "-mx-8", "2xl": "-mx-12",
};
export const BOX_MARGIN_Y_NEGATIVE: Record<NegativeSpacingToken, string> = {
  "2xs": "-my-0.5", xs: "-my-1", sm: "-my-2",
  base: "-my-3", md: "-my-4", lg: "-my-6", xl: "-my-8", "2xl": "-my-12",
};
export const BOX_MARGIN_TOP_NEGATIVE: Record<NegativeSpacingToken, string> = {
  "2xs": "-mt-0.5", xs: "-mt-1", sm: "-mt-2",
  base: "-mt-3", md: "-mt-4", lg: "-mt-6", xl: "-mt-8", "2xl": "-mt-12",
};
export const BOX_MARGIN_BOTTOM_NEGATIVE: Record<NegativeSpacingToken, string> = {
  "2xs": "-mb-0.5", xs: "-mb-1", sm: "-mb-2",
  base: "-mb-3", md: "-mb-4", lg: "-mb-6", xl: "-mb-8", "2xl": "-mb-12",
};
export const BOX_MARGIN_LEFT_NEGATIVE: Record<NegativeSpacingToken, string> = {
  "2xs": "-ml-0.5", xs: "-ml-1", sm: "-ml-2",
  base: "-ml-3", md: "-ml-4", lg: "-ml-6", xl: "-ml-8", "2xl": "-ml-12",
};
export const BOX_MARGIN_RIGHT_NEGATIVE: Record<NegativeSpacingToken, string> = {
  "2xs": "-mr-0.5", xs: "-mr-1", sm: "-mr-2",
  base: "-mr-3", md: "-mr-4", lg: "-mr-6", xl: "-mr-8", "2xl": "-mr-12",
};

export const BOX_DIRECTION = {
  row: "flex flex-row",
  column: "flex flex-col",
  "row-reverse": "flex flex-row-reverse",
  "column-reverse": "flex flex-col-reverse",
} as const;
export type FlexDirection = keyof typeof BOX_DIRECTION;

export const BOX_ALIGN_SELF = {
  auto: "self-auto", start: "self-start", center: "self-center",
  end: "self-end", stretch: "self-stretch", baseline: "self-baseline",
} as const;
export type FlexAlignSelf = keyof typeof BOX_ALIGN_SELF;

export const BOX_ALIGN = {
  start: "items-start", center: "items-center", end: "items-end",
  stretch: "items-stretch", baseline: "items-baseline",
} as const;
export type FlexAlign = keyof typeof BOX_ALIGN;

export const BOX_JUSTIFY = {
  start: "justify-start", center: "justify-center", end: "justify-end",
  between: "justify-between", around: "justify-around", evenly: "justify-evenly",
} as const;
export type FlexJustify = keyof typeof BOX_JUSTIFY;

// Every growing value carries `min-h-0 min-w-0`. A flex item's min-width and
// min-height default to `auto` — its CONTENT-based minimum — so `flex-1` on its
// own claims free space it can never give back: under negative free space the
// item stays at content size and overflows its container, and the container's
// `overflow: hidden` then clips the excess instead of a descendant scrolling
// it. Both axes are emitted because the automatic minimum only bites on the flex
// parent's MAIN axis, and a Box does not know its parent's direction — the
// other axis is inert. `none` and `initial` are excluded on purpose: they say
// "do not claim free space", so there is nothing to give back.
export const BOX_FLEX = {
  1: "flex-1 min-h-0 min-w-0",
  2: "flex-[2] min-h-0 min-w-0",
  3: "flex-[3] min-h-0 min-w-0",
  none: "flex-none",
  auto: "flex-auto min-h-0 min-w-0",
  initial: "flex-initial",
} as const;
export type FlexToken = keyof typeof BOX_FLEX;

// `grow` (flex-grow:1, basis auto) claims free space too — same pairing.
export const BOX_GROW = "grow min-h-0 min-w-0";

// ---------------------------------------------------------------------------
// Two-sided boolean maps
//
// A responsive boolean has to be able to turn OFF at a breakpoint, and a
// one-sided class cannot: `wrap={{ base: true, lg: false }}` needs something to
// emit at `lg`. Each entry therefore names both faces.
//
// The `false` face is reached ONLY through the object form. A scalar keeps the
// exact class string it emits today — `wrap={false}` stays silent — so wrapping
// these props in `Responsive<>` cannot change the output of a single existing
// call site.
// ---------------------------------------------------------------------------
export const BOX_WRAP: Record<"true" | "false", string> = {
  true: "flex-wrap",
  false: "flex-nowrap",
};
export const BOX_GROW_TOGGLE: Record<"true" | "false", string> = {
  true: BOX_GROW,
  false: "grow-0",
};
export const BOX_SHRINK: Record<"true" | "false", string> = {
  true: "shrink min-w-0",
  false: "shrink-0",
};
export const BOX_BORDER_TOGGLE: Record<"true" | "false", string> = {
  true: "border border-border",
  false: "border-0",
};

// Explicit display override. The flex-family props (direction/align/justify/
// wrap/gap) already opt a Box into display:flex, so `display` exists for the
// cases they cannot express: grid, contents, and forcing block back on.
export const BOX_DISPLAY = {
  block: "block", flex: "flex", "inline-flex": "inline-flex",
  grid: "grid", "inline-grid": "inline-grid",
  inline: "inline", "inline-block": "inline-block", contents: "contents",
} as const;
export type DisplayToken = keyof typeof BOX_DISPLAY;

// grid-template-columns. Static literals — Tailwind cannot see a computed one.
//
// `fill` is the fluid grid: as many equal tracks as fit at a 10rem minimum,
// stretched to share the row. A count keys on the VIEWPORT (`{ base, md }`),
// which is the wrong ruler for a grid inside a dialog pane or a sidebar-ed
// column: the pane can be 350px wide on a 1200px screen. `fill` measures the
// grid's own box, so the same call site yields one column in a narrow pane and
// four across a wide one. `auto-fill`, not `auto-fit`: two cards in a wide
// row keep card width instead of stretching to half the row each.
export const BOX_COLUMNS = {
  1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4",
  5: "grid-cols-5", 6: "grid-cols-6", 7: "grid-cols-7", 8: "grid-cols-8",
  9: "grid-cols-9", 10: "grid-cols-10", 11: "grid-cols-11", 12: "grid-cols-12",
  fill: "grid-cols-[repeat(auto-fill,minmax(10rem,1fr))]",
} as const;
export type ColumnsToken = keyof typeof BOX_COLUMNS;

// The `flex-1` members carry `min-h-0 min-w-0` for the reason spelled out on
// BOX_FLEX above. The pure-percentage members (`width`/`height`/`size`) do not:
// they never claim free space, so there is nothing to give back, and clearing
// their automatic minimum would let them collapse under a sibling.
export const BOX_FILL = {
  true: "flex-1 w-full min-h-0 min-w-0",
  width: "w-full",
  height: "h-full",
  // Both axes at 100% with NO flex-1 — for a box that fills a parent which
  // already has a definite size, rather than one that claims free space.
  size: "size-full",
  flex: "flex-1 min-h-0 min-w-0",
  absolute: "absolute inset-0",
  cell: "flex-1 w-full h-full self-stretch min-h-0 min-w-0",
  // display:contents — children join the PARENT's layout; for structural
  // wrappers (context-menu roots around flex cells) that must not disturb it.
  contents: "contents",
} as const;
export type FillOption = true | Exclude<keyof typeof BOX_FILL, "true">;

// ---------------------------------------------------------------------------
// Hover-reveal scopes
//
// A reveal is TWO classes on TWO elements: `group/<scope>` on the ancestor and
// `group-hover/<scope>:opacity-100` on the chrome. Tailwind cannot compose
// either name dynamically, and a typo on one side silently disables the reveal
// with no error. Both sides are therefore generated from this ONE list, so the
// pair can never drift. Add a scope here, not a hand-written class.
// ---------------------------------------------------------------------------
export const BOX_GROUP = {
  box: "group/box", tile: "group/tile", row: "group/row", col: "group/col",
  card: "group/card",
  colhead: "group/colhead", header: "group/header",
  resize: "group/resize", node: "group/node",
  token: "group/token", pane: "group/pane", option: "group/option",
  embed: "group/embed", address: "group/address", section: "group/section",
  addedge: "group/addedge", "inline-row": "group/inline-row",
  "inline-value-cell": "group/inline-value-cell",
  "sortable-row": "group/sortable-row", "settings-row": "group/settings-row",
  "menu-item": "group/menu-item", versionrow: "group/versionrow",
  tgroup: "group/tgroup", avatar: "group/avatar",
} as const;
export type GroupScope = keyof typeof BOX_GROUP;

// A press target owns its long press. Every `group` scope is one — a held
// finger ARMS it (`lib/touch-arm`), so the same hold must not also start a
// text selection, pop iOS's copy/look-up callout, or double-tap-zoom; and on a
// desktop a double-click on a row's title must not select the word, nor a
// reorder drag sweep a selection across the list. Text inside that IS content
// (a comment body in a card that also carries hover actions) opts back in
// with `Text selectable`; inputs and editors state `select-text` themselves,
// because Safari lets a no-select ancestor leak into an editable descendant.
export const PRESS_SURFACE_CLASS =
  "select-none [-webkit-touch-callout:none] touch-manipulation";

// Chrome that fades in when its `group={scope}` ancestor is hovered — or
// ARMED: `data-armed` is the group a touch tap highlighted (`lib/touch-arm`),
// the hover a coarse pointer cannot produce. Also revealed by keyboard focus
// and by an open popup, so the affordance never disappears mid-interaction.
// On a coarse pointer the hidden chrome is INERT (`pointer-events-none`)
// until one of those reveals it: a blind tap must land on the group and arm
// it, never on an invisible button. Every string is written out in full —
// Tailwind cannot see a class composed at run time.
export const BOX_HOVER_REVEAL: Record<GroupScope, string> = {
  box: "opacity-0 transition-opacity group-hover/box:opacity-100 group-data-armed/box:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/box:pointer-events-auto pointer-coarse:group-data-armed/box:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  tile: "opacity-0 transition-opacity group-hover/tile:opacity-100 group-data-armed/tile:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/tile:pointer-events-auto pointer-coarse:group-data-armed/tile:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  row: "opacity-0 transition-opacity group-hover/row:opacity-100 group-data-armed/row:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/row:pointer-events-auto pointer-coarse:group-data-armed/row:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  col: "opacity-0 transition-opacity group-hover/col:opacity-100 group-data-armed/col:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/col:pointer-events-auto pointer-coarse:group-data-armed/col:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  card: "opacity-0 transition-opacity group-hover/card:opacity-100 group-data-armed/card:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/card:pointer-events-auto pointer-coarse:group-data-armed/card:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  colhead: "opacity-0 transition-opacity group-hover/colhead:opacity-100 group-data-armed/colhead:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/colhead:pointer-events-auto pointer-coarse:group-data-armed/colhead:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  header: "opacity-0 transition-opacity group-hover/header:opacity-100 group-data-armed/header:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/header:pointer-events-auto pointer-coarse:group-data-armed/header:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  resize: "opacity-0 transition-opacity group-hover/resize:opacity-100 group-data-armed/resize:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/resize:pointer-events-auto pointer-coarse:group-data-armed/resize:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  node: "opacity-0 transition-opacity group-hover/node:opacity-100 group-data-armed/node:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/node:pointer-events-auto pointer-coarse:group-data-armed/node:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  token: "opacity-0 transition-opacity group-hover/token:opacity-100 group-data-armed/token:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/token:pointer-events-auto pointer-coarse:group-data-armed/token:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  pane: "opacity-0 transition-opacity group-hover/pane:opacity-100 group-data-armed/pane:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/pane:pointer-events-auto pointer-coarse:group-data-armed/pane:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  option: "opacity-0 transition-opacity group-hover/option:opacity-100 group-data-armed/option:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/option:pointer-events-auto pointer-coarse:group-data-armed/option:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  embed: "opacity-0 transition-opacity group-hover/embed:opacity-100 group-data-armed/embed:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/embed:pointer-events-auto pointer-coarse:group-data-armed/embed:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  address: "opacity-0 transition-opacity group-hover/address:opacity-100 group-data-armed/address:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/address:pointer-events-auto pointer-coarse:group-data-armed/address:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  section: "opacity-0 transition-opacity group-hover/section:opacity-100 group-data-armed/section:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/section:pointer-events-auto pointer-coarse:group-data-armed/section:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  addedge: "opacity-0 transition-opacity group-hover/addedge:opacity-100 group-data-armed/addedge:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/addedge:pointer-events-auto pointer-coarse:group-data-armed/addedge:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  "inline-row": "opacity-0 transition-opacity group-hover/inline-row:opacity-100 group-data-armed/inline-row:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/inline-row:pointer-events-auto pointer-coarse:group-data-armed/inline-row:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  "inline-value-cell": "opacity-0 transition-opacity group-hover/inline-value-cell:opacity-100 group-data-armed/inline-value-cell:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/inline-value-cell:pointer-events-auto pointer-coarse:group-data-armed/inline-value-cell:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  "sortable-row": "opacity-0 transition-opacity group-hover/sortable-row:opacity-100 group-data-armed/sortable-row:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/sortable-row:pointer-events-auto pointer-coarse:group-data-armed/sortable-row:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  "settings-row": "opacity-0 transition-opacity group-hover/settings-row:opacity-100 group-data-armed/settings-row:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/settings-row:pointer-events-auto pointer-coarse:group-data-armed/settings-row:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  "menu-item": "opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-data-armed/menu-item:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/menu-item:pointer-events-auto pointer-coarse:group-data-armed/menu-item:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  versionrow: "opacity-0 transition-opacity group-hover/versionrow:opacity-100 group-data-armed/versionrow:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/versionrow:pointer-events-auto pointer-coarse:group-data-armed/versionrow:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  tgroup: "opacity-0 transition-opacity group-hover/tgroup:opacity-100 group-data-armed/tgroup:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/tgroup:pointer-events-auto pointer-coarse:group-data-armed/tgroup:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
  // The avatar scrim is a decoration UNDER its own trigger, not a container of
  // one: the group element is the focused, popup-opening button — so the
  // keyboard-focus and open-popup reveals key off the group itself.
  avatar: "opacity-0 transition-opacity group-hover/avatar:opacity-100 group-data-armed/avatar:opacity-100 group-focus-visible/avatar:opacity-100 group-data-popup-open/avatar:opacity-100 focus-within:opacity-100 has-[[data-popup-open]]:opacity-100 pointer-coarse:pointer-events-none pointer-coarse:group-hover/avatar:pointer-events-auto pointer-coarse:group-data-armed/avatar:pointer-events-auto pointer-coarse:focus-within:pointer-events-auto pointer-coarse:has-[[data-popup-open]]:pointer-events-auto",
};

// The DISPLAY-based reveal: the chrome takes no room until its group is
// hovered, armed, focused within or holding an open popup, then lays out as
// a flex row. For chrome that must not occupy its slot at rest (a header's
// summoned actions beside a title that keeps the full width). Scopes are
// listed as surfaces need them — Tailwind scans literals.
export const BOX_HOVER_REVEAL_DISPLAY: Partial<Record<GroupScope, string>> = {
  box: "hidden group-hover/box:flex group-data-armed/box:flex focus-within:flex has-[[data-popup-open]]:flex",
  tile: "hidden group-hover/tile:flex group-data-armed/tile:flex focus-within:flex has-[[data-popup-open]]:flex",
};

// Ink that steps up to body foreground under the pointer: `self` on the
// text's own hover, a group scope on its `group={scope}` ancestor's (a
// pressable row whose sentence names where it goes — the words stay on the
// sentence's quiet rung at rest and light with the row). Touch has no hover:
// a HELD press arms the group and lights them the same way. Read by `Text
// hoverInk`; scopes are listed as surfaces need them.
export const TEXT_HOVER_INK = {
  self: "transition-colors hover:text-foreground",
  row: "transition-colors group-hover/row:text-foreground group-data-armed/row:text-foreground",
} as const satisfies Partial<Record<GroupScope | "self", string>>;

// Touch has no hover. Pairing this with `hoverReveal` keeps the chrome
// permanently visible — and pressable — on coarse pointers, in CSS, never by
// branching on a JS `isTouch` flag at the call site. The exception, not the
// rule: an affordance that is the group's whole point (a collapse arrow, an
// "Add banner" offer). Chrome a group merely carries waits for the arm.
export const BOX_REVEAL_ON_TOUCH =
  "pointer-coarse:opacity-100 pointer-coarse:pointer-events-auto";

/**
 * Every surface. Each is a hard-coded token a component declares outright —
 * `elevated` is a card floating over the page (a board tile, a dragged row) —
 * and `sidebar` is the navigation region, which sits beside the page but owns
 * the same family and paints the same way.
 *
 * A surface is a NAME, not an offset. It says which surface this element paints,
 * so it reads the same wherever the element is mounted — the whole reason the
 * contextual ladder it replaced is gone.
 *
 * THIS TUPLE IS THE ONE SOURCE OF THE SET. `SurfaceToken` derives from it, and
 * every map below is annotated `Record<SurfaceToken, …>`, so adding a surface
 * here is a compile error at each map until it is answered. Deriving the union
 * from a map instead is what let three maps drift: a map cannot both define the
 * union and be checked against it.
 */
export const SURFACES = [
  "sunken",
  "background",
  "card",
  "elevated",
  "popover",
  "sidebar",
] as const;
export type SurfaceToken = (typeof SURFACES)[number];

export const BOX_SURFACE: Record<SurfaceToken, string> = {
  sunken: "bg-sunken border-sunken-border shadow-sunken",
  background: "bg-background border-border shadow-background",
  card: "bg-card border-card-border shadow-card",
  elevated: "bg-elevated border-elevated-border shadow-elevated",
  popover: "bg-popover border-popover-border shadow-popover",
  sidebar: "bg-sidebar border-sidebar-border",
};

/** Shadow alone, for a Box that keeps one surface's fill and another's depth. */
export const BOX_SURFACE_SHADOW: Record<SurfaceToken, string> = {
  sunken: "shadow-sunken",
  background: "shadow-background",
  card: "shadow-card",
  elevated: "shadow-elevated",
  popover: "shadow-popover",
  // The sidebar is a plane of the page, not a card over it: no drop.
  sidebar: "",
};

/**
 * A fill that is decoration, not depth — a skeleton bar, an avatar well, a
 * progress track, a placeholder pool. Deliberately separate from the surfaces:
 * giving a 24px dot an elevation shadow is the failure in the other direction.
 */
type DecorativeBackground =
  | "muted"
  // `muted` as a scrim: a substrate that holds cards dimming its own contents.
  | "mutedVeil"
  // The `sunken` well as a scrim, for a well that holds cards (a board
  // column) dimming its own contents while a drop is refused.
  | "sunkenVeil"
  // The neutral tonal chip's fill (`Badge tone="tonal"`) as a plane: an
  // inline notice band that must read off a light page, where `muted` alone
  // is a smudge. It follows `--neutral-tint-share`, so it deepens in light only.
  | "tonal"
  | "primary"
  | "secondary"
  | "destructive"
  // The destructive tint: a row or well whose CONTENT is the problem (an
  // invalid value at rest) — the solid `destructive` is for a control that
  // does something destructive.
  | "destructiveSoft"
  | "success"
  | "warning"
  | "info"
  | "transparent"
  | "border"
  // Follow the PARENT's computed fill, hover wash included. For a
  // `position: sticky` child of a scrolling row: it must be opaque so the
  // content scrolling beneath it does not show through, and it must repaint
  // whatever state the row is in — a fixed fill would strand the pinned
  // lane at the resting color while the rest of the row washes.
  | "inherit";

export const BOX_BACKGROUND: Record<
  SurfaceToken | "selected" | DecorativeBackground,
  string
> = {
  sunken: "bg-sunken",
  background: "bg-background",
  card: "bg-card",
  elevated: "bg-elevated",
  popover: "bg-popover",
  sidebar: "bg-sidebar",
  // The selected wash. Selection is state a caller already holds, so it arrives
  // as a background rather than as a second prop; hover has its own because
  // `:hover` is CSS's to track. One value serves every surface — the wash is the
  // foreground color at low alpha and composites against whatever fill is already there.
  selected: "bg-wash-selected",
  muted: "bg-muted",
  mutedVeil: "bg-muted-veil",
  sunkenVeil: "bg-sunken-veil",
  tonal: "bg-c-tint",
  primary: "bg-primary",
  secondary: "bg-secondary",
  destructive: "bg-destructive",
  destructiveSoft: "bg-destructive/10",
  success: "bg-success",
  warning: "bg-warning",
  info: "bg-info",
  transparent: "bg-transparent",
  // The hairline-divider substrate: a 1px Box painted in the border color.
  border: "bg-border",
  inherit: "bg-inherit",
};
export type BackgroundToken = keyof typeof BOX_BACKGROUND;

/**
 * Interaction washes. ONE set for the whole system, because a wash is the
 * foreground color at low alpha: it composites against whatever surface is actually behind it, so
 * no surface needs its own copy and no element has to be told which surface it
 * is on.
 *
 * The alpha is also what makes washes STACK. A ghost button on a card that
 * itself washes on hover lands on top of the card's wash and still reads. A
 * pre-composited value cannot do that — it paints one flat color regardless of
 * what is under it, which is how a button on a hovered card ends up painting
 * the exact value its parent just painted and disappearing.
 *
 * These are whole literal strings because Tailwind only emits classes it can
 * read verbatim in source.
 */
export const WASH_HOVER = "hover:bg-wash-hover";
export const WASH_ACTIVE = "bg-wash-active";
export const WASH_SELECTED = "bg-wash-selected";
export const WASH_SELECTED_HOVER = "hover:bg-wash-selected-hover";
/** Pressed rung for a control that reports state through `data-pressed`. */
export const WASH_PRESSED = "data-pressed:bg-wash-active";
/** Highlighted rung for a keyboard-driven list — a menu or select item. */
export const WASH_HIGHLIGHTED = "data-highlighted:bg-wash-hover";

// The wash also paints the ARMED rung (`lib/touch-arm`): a tapped row on a
// coarse pointer wears the hover it cannot otherwise have.
export const WASH_ARMED = "data-armed:bg-wash-hover";
export const BOX_HOVER_BACKGROUND = `${WASH_HOVER} ${WASH_ARMED}`;
/** `Box selected`: the selected wash at rest, one rung deeper under the pointer. */
export const BOX_SELECTED = `${WASH_SELECTED} ${WASH_SELECTED_HOVER}`;
/** `Box dimmed`: the inactive fade, eased so a toggle does not snap. */
export const BOX_DIMMED = "opacity-40 transition-opacity duration-fast";

/**
 * The wash's two faces, for `hoverBackground` in its object form. The `false`
 * side paints the hover state transparent rather than emitting nothing: at a
 * breakpoint above one that switched the wash ON, silence would leave the
 * lower breakpoint's rule in force, which is the opposite of what was asked.
 */
export const BOX_HOVER_BACKGROUND_TOGGLE: Record<"true" | "false", string> = {
  true: `${WASH_HOVER} ${WASH_ARMED}`,
  false: "hover:bg-transparent data-armed:bg-transparent",
};

/** Scrim over a surface's own content — a drag veil, a disabled overlay. This
 *  one IS per-surface: a scrim hides what is beneath it, so it needs the
 *  surface's own opaque fill rather than a foreground wash over it. */
export const SURFACE_VEIL: Record<SurfaceToken | "muted", string> = {
  sunken: "bg-sunken-veil",
  background: "bg-background-veil",
  card: "bg-card-veil",
  elevated: "bg-elevated-veil",
  popover: "bg-popover-veil",
  sidebar: "bg-sidebar-veil",
  muted: "bg-muted-veil",
};
/** The opaque fill alone, for anything that must punch through content beneath
 *  it rather than blend with it — a sticky header, a pinned cell. */
export const SURFACE_BG: Record<SurfaceToken, string> = {
  sunken: "bg-sunken",
  background: "bg-background",
  card: "bg-card",
  elevated: "bg-elevated",
  popover: "bg-popover",
  sidebar: "bg-sidebar",
};

export const BOX_ROUNDED = {
  none: "rounded-none", xs: "rounded-xs", sm: "rounded-sm", md: "rounded-md",
  lg: "rounded-lg", xl: "rounded-xl", "2xl": "rounded-2xl",
  "3xl": "rounded-3xl", full: "rounded-full",
} as const;
export type RoundedToken = keyof typeof BOX_ROUNDED;

// Per-side radius, for a panel that butts flush against a neighbour: the two
// corners on the shared seam stay square so the join reads as one straight
// line. Paired with `rounded`, which names the radius; `roundedSide` names
// which corners get it. Written out per side because Tailwind only emits
// classes it can read as whole strings.
export type RoundedSide = "left" | "right" | "top" | "bottom";
export const BOX_ROUNDED_SIDE: Record<
  RoundedSide,
  Record<RoundedToken, string>
> = {
  left: {
    none: "rounded-s-none", xs: "rounded-s-xs", sm: "rounded-s-sm",
    md: "rounded-s-md", lg: "rounded-s-lg", xl: "rounded-s-xl",
    "2xl": "rounded-s-2xl", "3xl": "rounded-s-3xl", full: "rounded-s-full",
  },
  right: {
    none: "rounded-e-none", xs: "rounded-e-xs", sm: "rounded-e-sm",
    md: "rounded-e-md", lg: "rounded-e-lg", xl: "rounded-e-xl",
    "2xl": "rounded-e-2xl", "3xl": "rounded-e-3xl", full: "rounded-e-full",
  },
  top: {
    none: "rounded-t-none", xs: "rounded-t-xs", sm: "rounded-t-sm",
    md: "rounded-t-md", lg: "rounded-t-lg", xl: "rounded-t-xl",
    "2xl": "rounded-t-2xl", "3xl": "rounded-t-3xl", full: "rounded-t-full",
  },
  bottom: {
    none: "rounded-b-none", xs: "rounded-b-xs", sm: "rounded-b-sm",
    md: "rounded-b-md", lg: "rounded-b-lg", xl: "rounded-b-xl",
    "2xl": "rounded-b-2xl", "3xl": "rounded-b-3xl", full: "rounded-b-full",
  },
};

/**
 * Outer corner plus the matching inner-highlight corner, as one class pair, for
 * a panel that wears the 1px highlight ring (`Card`, `FramePanel`). The `before`
 * ring sits INSIDE the 1px border, so its radius is always the outer radius
 * minus 1px. Setting only the outer radius leaves the ring's `0 ∓1px` shadow
 * poking out as a crescent at each corner. Static strings: Tailwind only emits
 * what it can read whole in source.
 */
export const RING_RADIUS: Record<RoundedToken, string> = {
  none: "rounded-none before:rounded-none",
  xs: "rounded-xs before:rounded-[calc(var(--radius-xs)-1px)]",
  sm: "rounded-sm before:rounded-[calc(var(--radius-sm)-1px)]",
  md: "rounded-md before:rounded-[calc(var(--radius-md)-1px)]",
  lg: "rounded-lg before:rounded-[calc(var(--radius-lg)-1px)]",
  xl: "rounded-xl before:rounded-[calc(var(--radius-xl)-1px)]",
  "2xl": "rounded-2xl before:rounded-[calc(var(--radius-2xl)-1px)]",
  "3xl": "rounded-3xl before:rounded-[calc(var(--radius-3xl)-1px)]",
  full: "rounded-full before:rounded-full",
};

/**
 * The highlight ring itself: a hairline of light along the top edge in light
 * mode and along the bottom in dark, which is what separates two panels that
 * share a fill. Pair it with a `RING_RADIUS` entry.
 */
export const RING_HIGHLIGHT =
  "relative before:pointer-events-none before:absolute before:inset-0 before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]";

export type BorderEdge = "top" | "bottom" | "left" | "right";
export const BOX_BORDER_EDGE: Record<BorderEdge, string> = {
  top: "border-t border-border",
  bottom: "border-b border-border",
  left: "border-l border-border",
  right: "border-r border-border",
};

export const BOX_BORDER_COLOR = {
  border: "border-border",
  input: "border-input",
  // Reserves the 1px of border space with no visible line.
  transparent: "border-transparent",
  // An alpha veil of the ink, for an edge drawn on a ground that sits BELOW
  // every solid hairline token: the light `sidebar-rail` canvas (L 92.2) is
  // darker than `--border` (L 92.78), so a token-filled line inverts and
  // vanishes there. The veil composites darker than any ground it lands on
  // and follows a custom theme's ink. Light lands the line at the pane
  // seam's own lightness (~L 89 on the rail); dark needs the rail dividers'
  // 10% rung, because a white veil at 6% over a near-black ground is a
  // two-unit step nobody sees.
  // `bg-clip-padding` is load-bearing: a background paints under its border
  // by default, so without it the veil composites over the Box's OWN fill
  // (the near-white page) instead of the ground outside, and lands right
  // back at the lightness the hairline token vanished at.
  ink: "border-foreground/6 dark:border-foreground/10 bg-clip-padding",
  primary: "border-primary",
  // The workspace accent — the live-editing ring (`InlineEditingCell`), for a
  // host that paints that ring on its own chrome instead.
  brand: "border-brand",
  destructive: "border-destructive",
  // The invalid rung the form controls wear (`aria-invalid:border-destructive/36`
  // on Input, Select, Textarea) — a step quieter than `destructive` — for a
  // shell that frames a control the Field cannot mark itself, a rich-text
  // editor's bordered box beside a Select at the same rung.
  invalid: "border-destructive/36",
  warning: "border-warning",
} as const;
export type BorderColorToken = keyof typeof BOX_BORDER_COLOR;

export const BOX_POSITION = {
  relative: "relative", absolute: "absolute", fixed: "fixed", sticky: "sticky",
} as const;
export type PositionToken = keyof typeof BOX_POSITION;

// `clip` clips like `hidden` without making the element a scroll container,
// so descendants' `scrollIntoView` never scrolls the frame and `sticky`
// children keep working. Prefer it when the goal is clipping to a radius.
export const BOX_OVERFLOW = {
  hidden: "overflow-hidden", auto: "overflow-auto",
  scroll: "overflow-scroll", visible: "overflow-visible",
  clip: "overflow-clip",
} as const;
export type OverflowToken = keyof typeof BOX_OVERFLOW;

export const BOX_OVERFLOW_X: Record<OverflowToken, string> = {
  hidden: "overflow-x-hidden", auto: "overflow-x-auto",
  scroll: "overflow-x-scroll", visible: "overflow-x-visible",
  clip: "overflow-x-clip",
};
/** Scrollbar presentation for a scroller whose edges already say "more this
 * way" (a fade, a peeking next item). `hidden` covers both the standard
 * property and WebKit's pseudo-element — the app styles every
 * `::-webkit-scrollbar` with a fixed height, which turns Safari's overlay
 * bar into an always-painted strip, so `scrollbar-width` alone leaves it. */
export const BOX_SCROLLBAR = {
  hidden: "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
} as const;
export type ScrollbarToken = keyof typeof BOX_SCROLLBAR;

export const BOX_OVERFLOW_Y: Record<OverflowToken, string> = {
  hidden: "overflow-y-hidden", auto: "overflow-y-auto",
  scroll: "overflow-y-scroll", visible: "overflow-y-visible",
  clip: "overflow-y-clip",
};

export const BOX_BORDER_STYLE = {
  solid: "border-solid", dashed: "border-dashed",
  dotted: "border-dotted", none: "border-none",
} as const;
export type BorderStyleToken = keyof typeof BOX_BORDER_STYLE;

export const BOX_BORDER_WIDTH = {
  0: "border-0", 1: "border", 2: "border-2", 4: "border-4", 8: "border-8",
} as const;
export type BorderWidthToken = keyof typeof BOX_BORDER_WIDTH;

export const BOX_SHADOW = {
  none: "shadow-none", xs: "shadow-xs", sm: "shadow-sm", md: "shadow-md",
  lg: "shadow-lg", xl: "shadow-xl", "2xl": "shadow-2xl",
  // Theme-aware lift for a small control on a flat surface: dark drop in
  // light mode, faint white glow in dark (a black shadow vanishes there).
  control: "shadow-control",
} as const;
export type ShadowToken = keyof typeof BOX_SHADOW;

// Opacity steps actually in use. A closed set keeps the scale from sprawling
// into one-off percentages.
export const BOX_OPACITY = {
  0: "opacity-0", 10: "opacity-10", 20: "opacity-20", 30: "opacity-30",
  40: "opacity-40", 50: "opacity-50", 60: "opacity-60", 70: "opacity-70",
  80: "opacity-80", 90: "opacity-90", 100: "opacity-100",
} as const;
export type OpacityToken = keyof typeof BOX_OPACITY;

export const BOX_POINTER_EVENTS = {
  none: "pointer-events-none", auto: "pointer-events-auto",
} as const;
export type PointerEventsToken = keyof typeof BOX_POINTER_EVENTS;

export const BOX_USER_SELECT = {
  none: "select-none", text: "select-text", all: "select-all", auto: "select-auto",
} as const;
export type UserSelectToken = keyof typeof BOX_USER_SELECT;

/** Content clipped by the box dissolves at this edge over 1.5rem — the
 *  ScrollArea's `scrollFade` size — so a hard `maxHeight` cut reads as
 *  "there is more" rather than as a torn line. For a block that must NOT
 *  scroll on its own (it sits inside a scrolling drawer); a block that may
 *  scroll uses `ScrollArea scrollFade`. */
export const BOX_FADE = {
  bottom: "mask-b-from-[calc(100%-1.5rem)]",
  top: "mask-t-from-[calc(100%-1.5rem)]",
  y: "mask-t-from-[calc(100%-1.5rem)] mask-b-from-[calc(100%-1.5rem)]",
} as const;
export type FadeToken = keyof typeof BOX_FADE;

export const BOX_TOUCH_ACTION = {
  none: "touch-none", manipulation: "touch-manipulation",
  "pan-x": "touch-pan-x", "pan-y": "touch-pan-y", auto: "touch-auto",
} as const;
export type TouchActionToken = keyof typeof BOX_TOUCH_ACTION;

export const BOX_TRANSITION = {
  none: "transition-none", all: "transition-all", colors: "transition-colors",
  opacity: "transition-opacity", shadow: "transition-shadow",
  transform: "transition-transform",
  size: "transition-[width,height] motion-reduce:transition-none",
  // The SPACE the box takes in its parent's flow: its own size PLUS the
  // margins a collapsing box uses to give back the gap slot a flex parent
  // still pays for a zero-sized child. Animating size alone leaves that gap
  // behind as a phantom step, so the two properties belong to one token.
  space: "transition-[width,height,margin] motion-reduce:transition-none",
} as const;
export type TransitionToken = keyof typeof BOX_TRANSITION;

export const BOX_DURATION = {
  75: "duration-75", 100: "duration-fast", 150: "duration-150",
  200: "duration-base", 300: "duration-slow", 500: "duration-slower",
} as const;
export type DurationToken = keyof typeof BOX_DURATION;

export const BOX_SNAP_ALIGN = {
  start: "snap-start", center: "snap-center", end: "snap-end", none: "snap-align-none",
} as const;
export type SnapAlignToken = keyof typeof BOX_SNAP_ALIGN;

export const BOX_SNAP_TYPE = {
  x: "snap-x snap-mandatory", y: "snap-y snap-mandatory",
  "x-proximity": "snap-x snap-proximity", "y-proximity": "snap-y snap-proximity",
  none: "snap-none",
} as const;
export type SnapTypeToken = keyof typeof BOX_SNAP_TYPE;

export const BOX_CURSOR = {
  pointer: "cursor-pointer", default: "cursor-default",
  "not-allowed": "cursor-not-allowed", text: "cursor-text",
  grab: "cursor-grab", grabbing: "cursor-grabbing", "col-resize": "cursor-col-resize",
  crosshair: "cursor-crosshair",
} as const;
export type CursorToken = keyof typeof BOX_CURSOR;

// as="button" reset. Tailwind v4 preflight already zeroes button
// background/border/font/padding; this adds the pieces preflight leaves:
// UA centering and the missing pointer affordance.
/**
 * `Box as="button"` is layout, not a control: it has no focus design of its
 * own, so it also resets the UA focus outline that `* { outline-ring/50 }`
 * would otherwise paint on it. Components that want a keyboard focus treatment
 * (Button, Input, Checkbox, …) author `focus-visible:ring-*` themselves.
 */
export const BOX_BUTTON_RESET =
  "cursor-pointer text-start select-none outline-none";

// ---------------------------------------------------------------------------
// The responsive manifest
//
// Every class a Box can emit behind a prop that accepts `Responsive<T>`.
// `scripts/generate-box-responsive.ts` flattens this into the `@source inline()`
// block that forces Tailwind to emit the `sm:`/`md:`/… variants, which the
// scanner can never find on its own — the resolver composes them at runtime.
//
// A map missing from this list produces classes with NO CSS behind them: no
// type error, no lint error, no failing test, and nothing a reviewer can see.
// `box-responsive.test.tsx` re-runs the generator and diffs the checked-in
// output, which is the only thing standing between a new token and that
// silent failure.
//
// The hover-reveal maps are deliberately absent: `group`, `hoverReveal` and
// `revealOnTouch` are identity and behaviour, not style, so they stay scalar.
// ---------------------------------------------------------------------------
export const RESPONSIVE_CLASS_MAPS: Record<string, string>[] = [
  BOX_GAP, BOX_GAP_X, BOX_GAP_Y,
  BOX_PADDING, BOX_PADDING_X, BOX_PADDING_Y,
  BOX_PADDING_TOP, BOX_PADDING_BOTTOM, BOX_PADDING_LEFT, BOX_PADDING_RIGHT,
  BOX_MARGIN, BOX_MARGIN_X, BOX_MARGIN_Y,
  BOX_MARGIN_TOP, BOX_MARGIN_BOTTOM, BOX_MARGIN_LEFT, BOX_MARGIN_RIGHT,
  BOX_MARGIN_X_NEGATIVE, BOX_MARGIN_Y_NEGATIVE, BOX_MARGIN_TOP_NEGATIVE,
  BOX_MARGIN_BOTTOM_NEGATIVE, BOX_MARGIN_LEFT_NEGATIVE, BOX_MARGIN_RIGHT_NEGATIVE,
  BOX_DIRECTION, BOX_ALIGN, BOX_ALIGN_SELF, BOX_JUSTIFY,
  BOX_FLEX, BOX_DISPLAY, BOX_COLUMNS, BOX_FILL,
  BOX_WRAP, BOX_GROW_TOGGLE, BOX_SHRINK, BOX_BORDER_TOGGLE,
  BOX_HOVER_BACKGROUND_TOGGLE,
  BOX_SURFACE, BOX_SURFACE_SHADOW, BOX_BACKGROUND,
  BOX_ROUNDED, ...Object.values(BOX_ROUNDED_SIDE),
  BOX_BORDER_EDGE, BOX_BORDER_COLOR, BOX_BORDER_STYLE, BOX_BORDER_WIDTH,
  BOX_POSITION, BOX_OVERFLOW, BOX_OVERFLOW_X, BOX_OVERFLOW_Y,
  BOX_SHADOW, BOX_OPACITY,
  BOX_POINTER_EVENTS, BOX_USER_SELECT, BOX_TOUCH_ACTION, BOX_FADE,
  BOX_SNAP_ALIGN, BOX_SNAP_TYPE,
  BOX_TRANSITION, BOX_DURATION, BOX_CURSOR,
  // `hidden` has no map of its own: its true face is this one class and its
  // false face is whatever display the Box resolves to at that breakpoint,
  // which BOX_DISPLAY and BOX_DIRECTION above already cover.
  { hidden: "hidden" },
];
